import { lazy } from 'react';
import { createBrowserRouter, createHashRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppShell } from '@/components/AppShell';
import { RouteError } from '@/components/RouteError';
import { isNative } from '@/lib/platform';

const LoginPage = lazy(() =>
  import('@/features/auth/LoginPage').then((module) => ({
    default: module.LoginPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/ForgotPasswordPage').then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/ResetPasswordPage').then((module) => ({
    default: module.ResetPasswordPage,
  })),
);
const InvitePage = lazy(() =>
  import('@/features/auth/InvitePage').then((module) => ({
    default: module.InvitePage,
  })),
);
const AccountPage = lazy(() =>
  import('@/features/auth/AccountPage').then((module) => ({
    default: module.AccountPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@/features/families/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  })),
);
const MapPage = lazy(() =>
  import('@/features/map/MapPage').then((module) => ({
    default: module.MapPage,
  })),
);
const MembersPage = lazy(() =>
  import('@/features/families/MembersPage').then((module) => ({
    default: module.MembersPage,
  })),
);
const SearchPage = lazy(() =>
  import('@/features/search/SearchPage').then((module) => ({
    default: module.SearchPage,
  })),
);
const TreePage = lazy(() =>
  import('@/features/tree/TreePage').then((module) => ({
    default: module.TreePage,
  })),
);
const PersonPage = lazy(() =>
  import('@/features/persons/PersonPage').then((module) => ({
    default: module.PersonPage,
  })),
);
const ChatsPage = lazy(() =>
  import('@/features/chats/ChatsPage').then((module) => ({
    default: module.ChatsPage,
  })),
);
const ChatPage = lazy(() =>
  import('@/features/chats/ChatPage').then((module) => ({
    default: module.ChatPage,
  })),
);
const DiaryPage = lazy(() =>
  import('@/features/diary/DiaryPage').then((module) => ({
    default: module.DiaryPage,
  })),
);
const NotificationsPage = lazy(() =>
  import('@/features/notifications/NotificationsPage').then((module) => ({
    default: module.NotificationsPage,
  })),
);
const OfflinePage = lazy(() =>
  import('@/features/offline/OfflinePage').then((module) => ({
    default: module.OfflinePage,
  })),
);
const NotFoundPage = lazy(() =>
  import('@/features/offline/NotFoundPage').then((module) => ({
    default: module.NotFoundPage,
  })),
);
const PublicPersonPage = lazy(() =>
  import('@/features/public/PublicPersonPage').then((module) => ({
    default: module.PublicPersonPage,
  })),
);

const routes = [
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password/:token', element: <ResetPasswordPage /> },
  { path: '/invite/:token', element: <InvitePage /> },
  { path: '/offline', element: <OfflinePage /> },
  { path: '/p/:slug', element: <PublicPersonPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          {
            path: 'families/:familyId',
            element: <Navigate to="tree" replace />,
          },
          { path: 'families/:familyId/tree', element: <TreePage /> },
          {
            path: 'families/:familyId/spaces',
            element: <Navigate to="/account#spaces" replace />,
          },
          { path: 'families/:familyId/members', element: <MembersPage /> },
          { path: 'families/:familyId/map', element: <MapPage /> },
          { path: 'families/:familyId/search', element: <SearchPage /> },
          {
            path: 'families/:familyId/persons/:personId',
            element: <PersonPage />,
          },
          { path: 'families/:familyId/chats', element: <ChatsPage /> },
          {
            path: 'families/:familyId/chats/:chatId',
            element: <ChatPage />,
          },
          { path: 'families/:familyId/diary', element: <DiaryPage /> },
          { path: 'notifications', element: <NotificationsPage /> },
          { path: 'account', element: <AccountPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
];

const routesWithError = [{ errorElement: <RouteError />, children: routes }];

export const router = isNative()
  ? createHashRouter(routesWithError)
  : createBrowserRouter(routesWithError);
